// batch-59 loonstrook-controles (FO-433) — gedeelde DTO's + Zod-schemas.
//
// Eén contract voor de API (`routes/loonstrook-controles.ts`) en de backoffice
// (`/payroll/loonstroken/controles`, `/boete-verrekening`, `/verzoeken`), zodat
// een formulier nooit iets kan sturen dat de API weigert (CLAUDE.md "Validatie
// & foutmeldingen").
//
// Drie onderdelen:
//   A. het controle-rapport per verloningsrun (voertuig + bijtelling);
//   B. de boete-verrekening-bewaking;
//   C. de verzoeken-lijst over álle stroken (de zichtbaarheid die ontbrak).
//
// PRIVACY: bijtellingbedragen zijn gevoelig salarisdetail en zitten uitsluitend
// in de DTO's die achter `requireLoonstrokenToegang` worden geserveerd; ze
// belanden NOOIT in een melding, log of richting apps/klant.

import { z } from 'zod'

// ─── A. Controle-rapport ─────────────────────────────────────────────────────

export const LOONSTROOK_CONTROLE_AFWIJKING_TYPE = [
  'VOERTUIG_KENTEKEN_AFWIJKING',
  'VOERTUIG_OP_STROOK_ZONDER_KOPPELING',
  'VOERTUIG_GEKOPPELD_NIET_OP_STROOK',
  'BIJTELLING_OP_STROOK_ZONDER_REGISTRATIE',
  'BIJTELLING_GEREGISTREERD_NIET_OP_STROOK',
  'NIET_VAST_TE_STELLEN',
] as const
export type LoonstrookControleAfwijkingType =
  (typeof LOONSTROOK_CONTROLE_AFWIJKING_TYPE)[number]

export const LOONSTROOK_CONTROLE_AFWIJKING_TYPE_LABELS: Record<
  LoonstrookControleAfwijkingType,
  string
> = {
  VOERTUIG_KENTEKEN_AFWIJKING:             'Ander kenteken op de strook',
  VOERTUIG_OP_STROOK_ZONDER_KOPPELING:     'Voertuig op de strook, niet gekoppeld',
  VOERTUIG_GEKOPPELD_NIET_OP_STROOK:       'Voertuig gekoppeld, niet op de strook',
  BIJTELLING_OP_STROOK_ZONDER_REGISTRATIE: 'Bijtelling op de strook, geen registratie',
  BIJTELLING_GEREGISTREERD_NIET_OP_STROOK: 'Bijtelling geregistreerd, niet op de strook',
  NIET_VAST_TE_STELLEN:                    'Niet vast te stellen',
}

/**
 * De types die als échte afwijking tellen. `NIET_VAST_TE_STELLEN` staat er
 * bewust NIET in: een onleesbare strook is geen fout van de registratie
 * (spec §6.5 / T9) en mag de afwijking-teller nooit vullen.
 */
export const LOONSTROOK_CONTROLE_ECHTE_AFWIJKINGEN: readonly LoonstrookControleAfwijkingType[] = [
  'VOERTUIG_KENTEKEN_AFWIJKING',
  'VOERTUIG_OP_STROOK_ZONDER_KOPPELING',
  'VOERTUIG_GEKOPPELD_NIET_OP_STROOK',
  'BIJTELLING_OP_STROOK_ZONDER_REGISTRATIE',
  'BIJTELLING_GEREGISTREERD_NIET_OP_STROOK',
]

/** Telt dit type mee als afwijking (i.p.v. "niet vast te stellen")? */
export function isEchteAfwijking(type: LoonstrookControleAfwijkingType): boolean {
  return LOONSTROOK_CONTROLE_ECHTE_AFWIJKINGEN.includes(type)
}

export const LOONSTROOK_CONTROLE_AFWIJKING_STATUS = [
  'OPEN',
  'DOORGEGEVEN_SALARISVERWERKER',
  'REGISTRATIE_BIJGEWERKT',
  'GEEN_ACTIE',
] as const
export type LoonstrookControleAfwijkingStatus =
  (typeof LOONSTROOK_CONTROLE_AFWIJKING_STATUS)[number]

export const LOONSTROOK_CONTROLE_AFWIJKING_STATUS_LABELS: Record<
  LoonstrookControleAfwijkingStatus,
  string
> = {
  OPEN:                         'Open',
  DOORGEGEVEN_SALARISVERWERKER: 'Doorgegeven aan de salarisverwerker',
  REGISTRATIE_BIJGEWERKT:       'Registratie bijgewerkt',
  GEEN_ACTIE:                   'Geen actie nodig',
}

/** Eén afwijking in het rapport. Bedragen alleen achter de loonstrook-gate. */
export interface LoonstrookControleAfwijkingDTO {
  id: string
  rapportId: string
  loonstrookId: string
  employeeId: string
  medewerkerNaam: string | null
  type: LoonstrookControleAfwijkingType
  typeLabel: string
  status: LoonstrookControleAfwijkingStatus
  statusLabel: string
  strookWaarde: string | null
  appWaarde: string | null
  strookKenteken: string | null
  appKenteken: string | null
  /** Bijtelling per maand op de strook (EUR), of null. */
  bedragStrook: number | null
  /** Bijtelling per maand volgens de registratie (EUR), of null. */
  bedragApp: number | null
  voertuigId: string | null
  toelichting: string | null
  afgehandeldDoorNaam: string | null
  afgehandeldOp: string | null
  afhandelNotitie: string | null
  createdAt: string
}

/** Kop van één controle-rapport (lijst-rij). */
export interface LoonstrookControleRapportDTO {
  id: string
  jaar: number
  maand: number | null
  periodeNummer: number | null
  soort: 'LOONSTROOK' | 'JAAROPGAVE'
  runNummer: number
  runSleutel: string
  /** Leesbare periode-aanduiding ("juni 2026", "periode 7 2026"). */
  periodeLabel: string
  aantalStroken: number
  aantalAfwijkingen: number
  /** Openstaande (nog niet afgehandelde) echte afwijkingen. */
  aantalOpen: number
  aantalNietVastTeStellen: number
  drempelWaarschuwing: boolean
  /** FO-444 — stroken met een voertuigvermelding, en hoeveel daarvan automatisch gematcht zijn. */
  aantalMetVermelding: number
  aantalAutomatischGematcht: number
  aantalBoetesVerrekend: number
  aantalBoetesOpen: number
  bedragBoetesOpen: number
  gedraaidOp: string
  gemeldOp: string | null
  boeteGemeldOp: string | null
}

// ─── FO-444 (spec 144) — per-strook controle-lijst + leer-mappings ───────────

export const LOONSTROOK_CONTROLE_UITKOMST = ['KLOPT', 'AFWIJKING', 'NIET_VAST_TE_STELLEN'] as const
export type LoonstrookControleUitkomst = (typeof LOONSTROOK_CONTROLE_UITKOMST)[number]

export const LOONSTROOK_CONTROLE_UITKOMST_LABELS: Record<LoonstrookControleUitkomst, string> = {
  KLOPT:                'Klopt',
  AFWIJKING:            'Afwijking',
  NIET_VAST_TE_STELLEN: 'Niet vast te stellen',
}

export const LOONSTROOK_VOERTUIG_MATCH_SLEUTEL = [
  'KENTEKEN',
  'LEER_MAPPING',
  'NMBRS_REFERENTIE',
  'MERK_MODEL',
  'CATALOGUSWAARDE',
  'BIJTELLINGSBEDRAG',
  'ENIG_VOERTUIG',
] as const
export type LoonstrookVoertuigMatchSleutel = (typeof LOONSTROOK_VOERTUIG_MATCH_SLEUTEL)[number]

export const LOONSTROOK_VOERTUIG_MATCH_SLEUTEL_LABELS: Record<LoonstrookVoertuigMatchSleutel, string> = {
  KENTEKEN:          'Kenteken op de strook',
  LEER_MAPPING:      'Eerder handmatig gekoppeld',
  NMBRS_REFERENTIE:  'NMBRS-voertuigreferentie',
  MERK_MODEL:        'Merk/model op de strook',
  CATALOGUSWAARDE:   'Cataloguswaarde op de strook',
  BIJTELLINGSBEDRAG: 'Bijtellingsbedrag',
  ENIG_VOERTUIG:     'Enig gekoppeld voertuig (identiteit niet op de strook)',
}

// ─── batch-66 voertuigcontrole (FO-489) — cascade-status + herstelactie ──────

/**
 * De uitkomst van de voertuig-matcher-cascade per strook. `GEEN` is dus GEEN
 * controle-uitkomst (`LoonstrookControleUitkomst`): het zegt dat de cascade bij
 * de vermelding op de strook geen voertuig vond.
 */
export const LOONSTROOK_VOERTUIG_MATCH_STATUS = ['MATCH', 'GEEN', 'NIET_EENDUIDIG'] as const
export type LoonstrookVoertuigMatchStatus = (typeof LOONSTROOK_VOERTUIG_MATCH_STATUS)[number]

export const LOONSTROOK_VOERTUIG_MATCH_STATUS_LABELS: Record<LoonstrookVoertuigMatchStatus, string> = {
  MATCH:          'Voertuig herkend',
  GEEN:           'Geen voertuig gevonden',
  NIET_EENDUIDIG: 'Meerdere voertuigen mogelijk',
}

export const LOONSTROOK_HERSTEL_ACTIE = ['LEER_MAPPING', 'KOPPEL_VOERTUIG', 'VOERTUIG_TOEVOEGEN'] as const
export type LoonstrookHerstelActie = (typeof LOONSTROOK_HERSTEL_ACTIE)[number]

/**
 * Wat de beheerder kan doen bij een strook zonder herkend voertuig. De server
 * bepaalt de actie (`bepaalStrookHerstel` in `@streep/utils`), zodat de UI nooit
 * een knop toont die niets oplost.
 */
export interface LoonstrookStrookHerstelDTO {
  actie: LoonstrookHerstelActie
  uitleg: string
  /** Het kenteken op de strook in leesbare vorm, of null. */
  kenteken: string | null
  /** Het voertuig in het wagenpark met dat kenteken (doel van de koppelknop), of null. */
  voertuig: { id: string; kenteken: string } | null
}

/** Een gekoppeld voertuig waaruit de beheerder kan kiezen bij handmatig koppelen. */
export interface LoonstrookVoertuigKandidaatDTO {
  voertuigId: string
  kenteken: string
  merk: string | null
  model: string | null
}

/**
 * Eén strook in de controle-lijst: wat er op de strook gelezen is, of het
 * klopt en via welke sleutel (spec 144 par. 3.A.1).
 */
export interface LoonstrookControleStrookDTO {
  id: string
  rapportId: string
  loonstrookId: string
  employeeId: string
  medewerkerNaam: string | null
  uitkomst: LoonstrookControleUitkomst
  uitkomstLabel: string
  /** Letterlijk wat NMBRS afdrukt ("Gelezen op de strook: ..."). */
  strookVermelding: string | null
  strookKenteken: string | null
  strookMerkModel: string | null
  strookCataloguswaarde: number | null
  matchSleutel: LoonstrookVoertuigMatchSleutel | null
  matchSleutelLabel: string | null
  voertuigId: string | null
  voertuigKenteken: string | null
  appKentekens: string | null
  mappingId: string | null
  toelichting: string | null
  /** De in de periode gekoppelde voertuigen (keuzelijst voor handmatig koppelen). */
  kandidaten: LoonstrookVoertuigKandidaatDTO[]
  /**
   * batch-66 (FO-489) — de uitkomst van de matcher-cascade; null = de cascade
   * draaide niet (geen vermelding op de strook, of de strook is niet uitgelezen).
   */
  matchStatus: LoonstrookVoertuigMatchStatus | null
  matchStatusLabel: string | null
  /** De herstelactie bij GEEN / NIET_EENDUIDIG; null = niets te herstellen. */
  herstel: LoonstrookStrookHerstelDTO | null
}

/** Rapport-detail: kop + alle regels. */
export interface LoonstrookControleRapportDetailDTO extends LoonstrookControleRapportDTO {
  afwijkingen: LoonstrookControleAfwijkingDTO[]
  /** FO-444 — per strook de gelezen vermelding en de uitkomst. */
  stroken: LoonstrookControleStrookDTO[]
}

/**
 * Body bij POST /payroll/loonstroken/controles/voertuig-mappings - handmatig
 * "deze vermelding = dit voertuig" op één strook; wordt als leer-mapping
 * (medewerker x genormaliseerde vermelding) onthouden en de run herdraait.
 */
export const loonstrookVoertuigKoppelSchema = z.object({
  loonstrookId: z.string().min(1, 'Kies de loonstrook'),
  voertuigId: z.string().min(1, 'Kies het voertuig'),
})
export type LoonstrookVoertuigKoppelInput = z.infer<typeof loonstrookVoertuigKoppelSchema>

export const LOONSTROOK_VOERTUIG_MAPPING_STATUS = ['ACTIEF', 'TE_HERBEVESTIGEN', 'OPGEHEVEN'] as const
export type LoonstrookVoertuigMappingStatus = (typeof LOONSTROOK_VOERTUIG_MAPPING_STATUS)[number]

export const LOONSTROOK_VOERTUIG_MAPPING_STATUS_LABELS: Record<LoonstrookVoertuigMappingStatus, string> = {
  ACTIEF:           'Actief',
  TE_HERBEVESTIGEN: 'Te herbevestigen',
  OPGEHEVEN:        'Opgeheven',
}

/** Eén leer-mapping in het beheer-lijstje. */
export interface LoonstrookVoertuigMappingDTO {
  id: string
  employeeId: string
  medewerkerNaam: string | null
  vermeldingNormalized: string
  voertuigId: string
  voertuigKenteken: string | null
  voertuigOmschrijving: string | null
  status: LoonstrookVoertuigMappingStatus
  statusLabel: string
  bevestigdDoorNaam: string | null
  bevestigdOp: string
  laatstGebruiktOp: string | null
  aantalGebruikt: number
  herbevestigenReden: string | null
  opgehevenOp: string | null
  opgehevenReden: string | null
  /** Voertuigen die nu aan de medewerker gekoppeld zijn (voor herbevestigen). */
  kandidaten: LoonstrookVoertuigKandidaatDTO[]
}

/** Body bij POST .../voertuig-mappings/:id/opheffen. */
export const loonstrookVoertuigMappingOpheffenSchema = z.object({
  reden: z.string().trim().max(500).nullable().optional(),
})
export type LoonstrookVoertuigMappingOpheffenInput = z.infer<typeof loonstrookVoertuigMappingOpheffenSchema>

/** Body bij POST .../voertuig-mappings/:id/herbevestigen (optioneel ander voertuig). */
export const loonstrookVoertuigMappingHerbevestigenSchema = z.object({
  voertuigId: z.string().min(1).nullable().optional(),
})
export type LoonstrookVoertuigMappingHerbevestigenInput = z.infer<
  typeof loonstrookVoertuigMappingHerbevestigenSchema
>

/** Acceptatiegrens uit spec 144 par. 3.A.4: aandeel automatisch gematchte stroken. */
export const LOONSTROOK_AUTOMATCH_ACCEPTATIEGRENS = 0.9

/**
 * Body bij POST /payroll/loonstroken/controles/afwijkingen/:id/afhandelen.
 *
 * `DOORGEGEVEN_SALARISVERWERKER` = "de app klopt": de bevinding is doorgezet naar
 * de salarisverwerker. LET OP - deze route VERSTUURT NIETS. Het doorgeven zelf
 * loopt via de bestaande FO-417-afkeurflow op de loonstrook (of buiten de app om);
 * deze status legt alleen vast DAT het gebeurd is. Daarom is de notitie hier
 * verplicht: zonder mail-/ticketreferentie sluit een afwijking op een belofte die
 * het systeem niet kan waarmaken, en weet niemand bij de volgende run of de
 * eerste ronde ooit verstuurd is.
 * `REGISTRATIE_BIJGEWERKT` = "de strook klopt": de berijder-/bijtelling-
 * registratie in de app is aangepast. `GEEN_ACTIE` vereist een toelichting -
 * anders verdwijnt een afwijking zonder dat iemand weet waarom.
 */
export const loonstrookControleAfhandelenSchema = z
  .object({
    status: z.enum(['DOORGEGEVEN_SALARISVERWERKER', 'REGISTRATIE_BIJGEWERKT', 'GEEN_ACTIE']),
    notitie: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.status === 'GEEN_ACTIE' && !(v.notitie ?? '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['notitie'],
        message: 'Leg vast waarom hier geen actie nodig is',
      })
    }
    if (v.status === 'DOORGEGEVEN_SALARISVERWERKER' && !(v.notitie ?? '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['notitie'],
        message: 'Noteer hoe en wanneer het is doorgegeven (mail- of ticketreferentie)',
      })
    }
  })
export type LoonstrookControleAfhandelenInput = z.infer<
  typeof loonstrookControleAfhandelenSchema
>

// ─── Inhoudingen op de strook (bron voor de boete-matcher) ───────────────────

/**
 * Categorie van een inhoudingsregel. `WETTELIJK` (loonheffing, pensioen, WIA,
 * WGA) wordt NOOIT als boete-inhouding gematcht: die regels staan op elke
 * strook en zouden anders toevallig op het boetebedrag kunnen uitkomen.
 */
export const LOONSTROOK_INHOUDING_CATEGORIE = [
  'BOETE',
  'VOORSCHOT',
  'VERREKENING',
  'WETTELIJK',
  'OVERIG',
] as const
export type LoonstrookInhoudingCategorie = (typeof LOONSTROOK_INHOUDING_CATEGORIE)[number]

/** Eén inhoudingsregel van de strook. `bedrag` is positief (het ingehouden bedrag). */
export interface LoonstrookInhouding {
  categorie: LoonstrookInhoudingCategorie
  omschrijving: string
  bedrag: number
}

export const LoonstrookInhoudingSchema = z.object({
  categorie: z.enum(LOONSTROOK_INHOUDING_CATEGORIE),
  omschrijving: z.string().trim().max(200),
  bedrag: z.number().finite(),
})
export const LoonstrookInhoudingenSchema = z.array(LoonstrookInhoudingSchema)

// ─── B. Boete-verrekening ────────────────────────────────────────────────────

export const BOETE_VERREKEN_STATUS = [
  'TE_VERREKENEN',
  'DEELS_VERREKEND',
  'VERREKEND',
  'AFGEBOEKT',
] as const
export type BoeteVerrekenStatus = (typeof BOETE_VERREKEN_STATUS)[number]

export const BOETE_VERREKEN_STATUS_LABELS: Record<BoeteVerrekenStatus, string> = {
  TE_VERREKENEN:   'Te verrekenen',
  DEELS_VERREKEND: 'Deels verrekend',
  VERREKEND:       'Verrekend',
  AFGEBOEKT:       'Afgeboekt',
}

/** De statussen die nog bewaakt worden (openstaand-overzicht + meldingen). */
export const BOETE_VERREKEN_OPEN_STATUSSEN: readonly BoeteVerrekenStatus[] = [
  'TE_VERREKENEN',
  'DEELS_VERREKEND',
]

export const BOETE_VERREKENING_BRON = ['AUTO_MATCH', 'HANDMATIG'] as const
export type BoeteVerrekeningBron = (typeof BOETE_VERREKENING_BRON)[number]

export const BOETE_VERREKENING_BRON_LABELS: Record<BoeteVerrekeningBron, string> = {
  AUTO_MATCH: 'Automatisch gekoppeld',
  HANDMATIG:  'Handmatig gekoppeld',
}

/** Eén (deel)verrekening bij een boete. */
export interface BoeteVerrekeningDTO {
  id: string
  boeteId: string
  loonstrookId: string
  runSleutel: string
  periodeLabel: string
  bedrag: number
  bron: BoeteVerrekeningBron
  bronLabel: string
  componentOmschrijving: string | null
  gekoppeldDoorNaam: string | null
  createdAt: string
}

/** Eén rij in het "openstaande boete-verrekeningen"-overzicht. */
export interface BoeteVerrekeningOverzichtDTO {
  boeteId: string
  employeeId: string | null
  medewerkerNaam: string | null
  /** True zolang de medewerker in dienst is; false = eindafrekening-aandacht. */
  inDienst: boolean
  voertuigId: string | null
  kenteken: string | null
  datum: string
  soortLabel: string
  kenmerk: string | null
  bedrag: number
  verrekendBedrag: number
  openBedrag: number
  status: BoeteVerrekenStatus
  statusLabel: string
  /** Ouderdom in dagen sinds de overtredingsdatum (NL-kalenderdagen). */
  ouderdomDagen: number
  laatsteBewakingRunSleutel: string | null
  verrekeningen: BoeteVerrekeningDTO[]
}

/**
 * Body bij POST /payroll/loonstroken/boete-verrekening/:boeteId/koppelen -
 * handmatig (her)koppelen aan een strook, ook voor een deelbedrag (§6.3).
 */
export const boeteVerrekeningKoppelSchema = z.object({
  loonstrookId: z.string().min(1, 'Kies de loonstrook waarop is ingehouden'),
  bedrag: z
    .number({ message: 'Vul een geldig bedrag in' })
    .positive('Bedrag moet groter dan 0 zijn')
    .max(1_000_000, 'Bedrag is onrealistisch hoog'),
  componentOmschrijving: z.string().trim().max(200).nullable().optional(),
})
export type BoeteVerrekeningKoppelInput = z.infer<typeof boeteVerrekeningKoppelSchema>

/**
 * Body bij DELETE .../boete-verrekening/:boeteId/verrekeningen/:id - een
 * koppeling terugdraaien. Reden optioneel (een misklik hoeft geen verhaal), maar
 * hij komt wél in de audit.
 */
export const boeteVerrekeningOntkoppelSchema = z.object({
  reden: z.string().trim().max(2000).nullable().optional(),
})
export type BoeteVerrekeningOntkoppelInput = z.infer<typeof boeteVerrekeningOntkoppelSchema>

/** Body bij POST /payroll/loonstroken/boete-verrekening/:boeteId/afboeken. */
export const boeteVerrekeningAfboekenSchema = z.object({
  // batch-66 (FO-497) — zelfde ondergrens als de statusmachine van de boeteflow
  // (afboeken vraagt een reden van minstens 5 tekens), zodat het formulier
  // nooit iets stuurt dat de API weigert.
  reden: z
    .string()
    .trim()
    .min(5, 'Geef een reden op (minstens 5 tekens); die blijft in de audit staan')
    .max(2000),
})
export type BoeteVerrekeningAfboekenInput = z.infer<typeof boeteVerrekeningAfboekenSchema>

// ─── C. Verzoeken-lijst (zichtbaarheid) ──────────────────────────────────────

/**
 * Eén rij in de verzoeken-lijst over álle loonstroken. Bewust breder dan
 * `LoonstrookVerzoekDTO` (die hoort bij één strook): hier staat óók de periode
 * en de ouderdom, zodat de lijst zonder doorklikken bruikbaar is. Geen bedragen,
 * geen BSN.
 */
export interface LoonstrookVerzoekLijstDTO {
  id: string
  loonstrookId: string
  employeeId: string
  medewerkerNaam: string | null
  type: 'VRAAG' | 'CORRECTIE'
  typeLabel: string
  status: 'OPEN' | 'BEANTWOORD' | 'AFGEHANDELD'
  statusLabel: string
  besluit: 'GOEDGEKEURD' | 'AFGEWEZEN' | null
  besluitLabel: string | null
  bericht: string
  periodeLabel: string
  jaar: number
  maand: number | null
  periodeNummer: number | null
  /** Ouderdom in NL-kalenderdagen sinds indienen. */
  ouderdomDagen: number
  createdAt: string
  afgehandeldOp: string | null
}

/** Tellers boven de verzoeken-lijst + de badge in de sub-tab-balk. */
export interface LoonstrookVerzoekTellerDTO {
  /** Nog niet beoordeeld (besluit == null) en niet afgehandeld. */
  open: number
  /** Beoordeeld maar nog niet afgerond. */
  inBehandeling: number
  totaal: number
}
